import { APP_MODULES, type AppModule } from '@shared/constants/system/app';

/**
 * Organisation engagement health (CUR-3112): the measured modules, the scoring constants and the
 * labels the System console shows. The scoring itself is server code; the client reads these for
 * labels and thresholds so the two cannot drift. The stored shape is `OrgEngagementHealth` in
 * `@shared/types/engagement`.
 */

/**
 * Modules whose use is measured, as changes to their records in the audit history. Attestations,
 * billing and the document tools other than auto-detect have no measure yet and are not scored.
 */
export const ENGAGEMENT_MODULES = [
    { key: 'claim', label: 'Claims', appModule: APP_MODULES.CLAIM },
    { key: 'compliance', label: 'Compliance', appModule: APP_MODULES.COMPLIANCE },
    { key: 'risk', label: 'Risk', appModule: APP_MODULES.RISK },
    { key: 'mspr', label: 'Material service providers', appModule: APP_MODULES.MSPR },
    { key: 'fleet', label: 'Fleet', appModule: APP_MODULES.FLEET },
    { key: 'auto-detect', label: 'Auto-detect', appModule: APP_MODULES.AUTO_DETECT },
] as const satisfies readonly { key: string; label: string; appModule: AppModule }[];

export type EngagementModuleKey = (typeof ENGAGEMENT_MODULES)[number]['key'];

export const ENGAGEMENT_MODULE_LABELS = Object.fromEntries(
    ENGAGEMENT_MODULES.map(({ key, label }) => [key, label]),
) as Record<EngagementModuleKey, string>;

/** Overall score from which an organisation is in each tier. */
export const ENGAGEMENT_TIER_THRESHOLDS = { high: 70, medium: 40 } as const;

/**
 * Engagement status (CUR-3113): what an organisation's health asks of Curium staff. Ordered from
 * best to worst for sorting; onboarding sits apart, judged on activation rather than volume.
 */
export const ENGAGEMENT_STATUSES = ['healthy', 'watch', 'at-risk', 'dormant', 'onboarding'] as const;

export type EngagementStatus = (typeof ENGAGEMENT_STATUSES)[number];

export const ENGAGEMENT_STATUS_LABELS = {
    healthy: 'Healthy',
    watch: 'Watch',
    'at-risk': 'At risk',
    dormant: 'Dormant',
    onboarding: 'Onboarding',
} as const satisfies Record<EngagementStatus, string>;

/** What each status means, for the page's help and the digest. */
export const ENGAGEMENT_STATUS_DESCRIPTIONS = {
    healthy: 'Overall health of 70 or more.',
    watch: 'Overall health from 40 to 69.',
    'at-risk': 'Overall health below 40.',
    dormant: 'No member has signed in or changed a record for 30 days.',
    onboarding: 'In the first 90 days: judged on getting started, not on volume.',
} as const satisfies Record<EngagementStatus, string>;

/** How a status is decided (`decideEngagementStatus` in the server). */
export const ENGAGEMENT_STATUS_RULES = {
    /** Days after the organisation was created during which it is onboarding. */
    onboardingDays: 90,
    /** Onboarding day from which a slow start raises "activation stalled". */
    activationCheckFromDay: 14,
    /** Days without any member activity after which an organisation is dormant. */
    dormantAfterDays: 30,
    /**
     * Days (UTC calendar days, one per nightly run) the readings must point away from the current
     * score-based status before it changes to the latest reading's status, so a status does not flap
     * on one day's reading. Becoming dormant, starting or ending onboarding apply at once.
     */
    confirmAfterDays: 7,
} as const;

/**
 * Scoring constants. Each is a share or a window, never a per-user volume target: an organisation is
 * measured against its own seats and its own recent weeks.
 */
export const ENGAGEMENT_SCORING = {
    /** Weights of the areas in the overall score; renormalised over the areas that could be scored. */
    areaWeights: { reach: 0.45, depth: 0.35, hygiene: 0.2 },
    reach: {
        /** Share of seats active in the last 7 days that scores full marks. */
        weeklyActiveTarget: 0.8,
        /** Share of seats active in the last 30 days that scores full marks. */
        monthlyActiveTarget: 0.9,
        /** Weight of the weekly share; the monthly share takes the rest. */
        weeklyWeight: 0.6,
    },
    depth: {
        /** Weeks before the last one that make the baseline. */
        baselineWeeks: 8,
        /** Share of the weekly baseline that scores full marks for a module. */
        baselineTarget: 0.75,
        /** Score of a module with no baseline that was used in the last 30 days but not last week. */
        recentlyUsedScore: 50,
    },
    hygiene: {
        /** An open claim with no change in this many days counts as stale. */
        staleClaimDays: 60,
        /** An open complaint older than this many days counts as ageing (the standard IDR timeframe). */
        ageingComplaintDays: 30,
        /** A classified auto-detect message unreviewed for this many days counts as backlog. */
        autoDetectBacklogDays: 7,
        /** How far back auto-detect messages are counted. */
        autoDetectWindowDays: 90,
    },
    /** Change of the overall score within which the trend reads as stable. */
    stableTrendBand: 5,
} as const;

/** What each hygiene check means, for the page and the digest. */
export const ENGAGEMENT_HYGIENE_CHECK_LABELS = {
    'stale-claims': 'Open claims with no activity for 60+ days',
    'overdue-tasks': 'Overdue tasks',
    'ageing-complaints': 'Complaints open for more than 30 days',
    'auto-detect-backlog': 'Auto-detect messages unreviewed for 7+ days',
} as const;

export const ENGAGEMENT_AREA_LABELS = {
    reach: 'Reach',
    depth: 'Depth',
    hygiene: 'Hygiene',
} as const;

/** One-line explanation of each area, for the page's "How health is measured" help. */
export const ENGAGEMENT_AREA_DESCRIPTIONS = {
    reach: 'Are the people with seats using Curium?',
    depth: 'Are the enabled modules in use, compared with recent weeks?',
    hygiene: 'Is Curium being used correctly: claims, tasks and complaints kept moving?',
} as const;
